import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@synthesis/shared-utils";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import {
  applicantDataSchema,
  isIndividualApplicantType,
  scrollToFirstInvalidCreateApplicationField,
  usePageStore,
  type ApplicantDataFormValues,
} from "../../create-application-page.lib";
import { ApplicantBankSection } from "./create-application-applicant-bank-section";
import { ApplicantContactSection } from "./create-application-applicant-contact-section";
import { ApplicantPayerSection } from "./create-application-applicant-payer-section";
import { ApplicantRegistrationSection } from "./create-application-applicant-registration-section";
import { ApplicantSignerSection } from "./create-application-applicant-signer-section";

type CreateApplicationApplicantStepProps = {
  className?: string;
};

export const CreateApplicationApplicantStep = observer(({ className }: CreateApplicationApplicantStepProps) => {
  const pageStore = usePageStore();

  const methods = useForm<ApplicantDataFormValues>({
    resolver: zodResolver(applicantDataSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: pageStore?.applicantData,
  });

  const applicantType = useWatch({ control: methods.control, name: "applicantType" });
  const paymentByAnotherOrganization = useWatch({ control: methods.control, name: "paymentByAnotherOrganization" });

  const { handleSubmit, watch, formState } = methods;

  useEffect(() => {
    pageStore?.setApplicantDataValidator(
      () =>
        new Promise<boolean>((resolve) => {
          void handleSubmit(
            () => resolve(true),
            () => {
              scrollToFirstInvalidCreateApplicationField();
              resolve(false);
            },
          )();
        }),
    );

    return () => pageStore?.setApplicantDataValidator(null);
  }, [pageStore, handleSubmit]);

  useEffect(() => {
    pageStore?.setApplicantStepValid(formState.isValid);
  }, [formState.isValid, pageStore]);

  useEffect(() => {
    const subscription = watch((values) => {
      pageStore?.setApplicantData(values as ApplicantDataFormValues);
    });

    return () => subscription.unsubscribe();
  }, [pageStore, watch]);

  return (
    <FormProvider {...methods}>
      <form className={cn("bg-neutrals-white flex flex-col gap-8 rounded-[14px] px-6 py-8", className)}>
        <ApplicantRegistrationSection applicantType={applicantType} />
        <ApplicantContactSection applicantType={applicantType} />

        {!isIndividualApplicantType(applicantType) && (
          <>
            <ApplicantSignerSection />
            <ApplicantBankSection showAnotherPayerCheckbox />
            {paymentByAnotherOrganization && <ApplicantPayerSection />}
          </>
        )}
      </form>
    </FormProvider>
  );
});
